import { LeadCharacterSelect } from "@/components/LeadCharacterSelect";
import SunoImportRecovery from "@/components/SunoImportRecovery";
import { useSunoImportRecovery } from "@/hooks/useSunoImportRecovery";
/* The v2 landing's composer. Behaviour mirrors Landing.tsx's SimpleComposer
   (same song sources, reference images, options, draft persistence and
   project-creation path) in the v2 design's markup: the .composer / .chip /
   .drop / .menu classes are styled by landing-v2.css, and the pieces the
   design never had live in landing-v2-app.css. */
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { useAuth } from "@/_core/hooks/useAuth";
import { useLanguageRouter } from "@/hooks/useLanguageRouter";
import { trpc } from "@/lib/trpc";
import ReferencePickerModal, {
  MAX_REFERENCES,
  type ReferenceSelections,
} from "@/components/ReferencePickerModal";
import {
  PromptImageTagHint,
  usePromptImageTagMenu,
  type PromptImageTagOption,
} from "@/components/PromptImageTagMenu";
import { assignPromptImageTags } from "@shared/promptImageTags";
import { isStockCharacterPresetUrl } from "@shared/characterPresets";
import { toastMultiPersonCastPhoto } from "@/lib/multiPersonCastPhotoToast";
import LyricsStyleModal from "@/components/director/LyricsStyleModal";
import CopyrightConfirmDialog from "@/components/CopyrightConfirmDialog";
import UpgradeModal from "@/components/UpgradeModal";
import {
  captionConfigLabel,
  normalizeCaptionConfig,
  normalizeCaptionStyleId,
  type CaptionConfig,
  type CaptionStyleId,
} from "@/lib/captionStyles";
import { getPlanTierIndex } from "@/lib/pricingPlans";
import { uploadFileDirect, type UploadProgress } from "@/lib/directUpload";
import {
  AUDIO_UPLOAD_ACCEPT,
  MAX_AUDIO_UPLOAD_BYTES,
  MAX_AUDIO_UPLOAD_MB,
  isAcceptedAudioSourceFile,
} from "@/lib/audioUpload";
import {
  getUnsupportedAudioPageError,
  isEmailAddressLink,
} from "@/lib/audioLinkValidation";
import {
  audioOriginRequiresCopyrightConfirmation,
  hasAcknowledgedCopyright,
  isSunoAudioUrl,
  markCopyrightAcknowledged,
  type AudioOrigin,
} from "@/lib/copyrightConsent";
import {
  trackTryGenerateLyricVideo,
  trackTryGenerateVideo,
  trackVideoCopyrightConfirmationCancelled,
  trackVideoCopyrightConfirmationShown,
  trackVideoProjectInitiated,
  type AuthModalTrigger,
} from "@/lib/dataLayer";
import {
  clearVideoComposerDraft,
  deletePendingVideoComposerFile,
  hasVideoComposerDraftInput,
  loadPendingVideoComposerFile,
  loadVideoComposerDraft,
  savePendingVideoComposerFile,
  saveVideoComposerDraft,
  type VideoComposerDraft,
  type VideoComposerPendingFile,
} from "@/lib/videoComposerDraft";
import {
  LANDING_SAMPLE_SONGS,
  getYouTubeLinkError,
  inferVocalPresentationFromText,
  isSpotifyUrl,
  isTikTokUrl,
  isYouTubeUrl,
  normalizeVoiceGender,
  sourceAudioOrigin,
  vocalPresentationFromValue,
  type LibrarySong,
  type SourceState,
} from "@/lib/composerSource";
import type { DemoMusicVideo } from "@/data/demoMusicVideos";
import { defaultNewProjectModeFor } from "@/lib/directorModeGate";

export type ComposerV2Mode = "video" | "singing" | "dance" | "lyrics" | "song";

export type ComposerV2Handle = {
  /** Same as pressing the composer's own CTA (used by the sticky dock). */
  submit(): void;
  /** Open the "Select a song" menu in the composer. */
  openSongMenu(): void;
  /** Toggle the same song menu as a popover above the sticky dock. */
  toggleDockSongMenu(): void;
  /** Remix from the gallery popup: the demo's song, lead photo and prompt. */
  loadDemo(demo: DemoMusicVideo): void;
};

type Props = {
  mode: ComposerV2Mode;
  /** Opens the sign-in window, labelled with the gate that asked for it. */
  onRequireAuth: (trigger: AuthModalTrigger) => void;
  /** Current song label, or null — drives the dock's song slot. */
  onSourceChange?: (label: string | null) => void;
  /** Passed through to the design's entrance animation. */
  enterDelay?: string;
  /** Fires when the dock's song popover opens/closes (dock sets data-open). */
  onDockSongOpenChange?: (open: boolean) => void;
};

// Studio (step-by-step) is the only public project mode; see
// Landing.tsx / directorModeGate.ts. The Dance card creates a Director
// dance project (Studio stepper + choreography stage). The
// director_default_v1 "director" arm starts music videos in Director chat
// instead; Studio stays one click away inside the project.
const PROJECT_MODE = "fast_v2" as const;
const projectModeFor = (
  mode: ComposerV2Mode,
  directorDefaultArm?: string | null,
): "fast_v2" | "studio" | "dance" =>
  mode === "dance" ? "dance" : defaultNewProjectModeFor(directorDefaultArm);
// Same sentences DirectorHome appends for a dance project, so the brief and
// the choreography read the same intent whichever composer created it.
const DANCE_INTENT =
  "This is a dance video: the lead performs choreographed full-body dance to the song, with lip-sync hero shots on the sung lines.";
const DANCE_LOCK_LOOK =
  "Character look: keep the uploaded character's EXACT face, hairstyle, and outfit from the reference photo in every scene — do not restyle or change wardrobe between scenes (if the prompt or script names a costume, dress them in that instead of the photo's clothes).";

/** Field text that is (probably) a URL, as opposed to a typed prompt. */
function looksLikeLink(raw: string): boolean {
  const text = raw.trim();
  if (!text) return false;
  if (/^(https?:\/\/|www\.)/i.test(text)) return true;
  return (
    /\b[a-z0-9-]+\.(com|be|tv|ai|io|net|org|co|fm|app|link)(\/|\b)/i.test(text) &&
    !/\s/.test(text)
  );
}

const RESOLUTIONS: { id: string; label: string; paidOnly?: boolean }[] = [
  { id: "480p", label: "480p" },
  { id: "720p", label: "720p", paidOnly: true },
  { id: "1080p", label: "1080p", paidOnly: true },
];

type MenuId = "song" | "res" | "more" | "docksong";
// The dock renders this container; the song menu portals into it.
const DOCK_SONG_POP_ID = "dockSongPop";

// The link platforms rotate through the field's placeholder, as on the
// current landing page.
const LINK_PLATFORMS = ["Suno", "YouTube", "TikTok", "SoundCloud"];
const LINK_PLACEHOLDER_INTERVAL_MS = 1800;
/** The one field is a textarea that starts at one row (so pasted multi-line
 *  prompts keep their line breaks and stay readable) and grows to this many
 *  rows before scrolling. Enter submits; Shift+Enter inserts a newline. */
const FIELD_MAX_ROWS = 5;

const ComposerV2 = forwardRef<ComposerV2Handle, Props>(function ComposerV2(
  { mode, onRequireAuth, onSourceChange, enterDelay = "980ms", onDockSongOpenChange },
  ref,
) {
  const { t } = useTranslation();
  const { user, isAuthenticated } = useAuth();
  const { navigate } = useLanguageRouter();

  const initialDraft = useMemo(() => {
    const draft = loadVideoComposerDraft();
    return draft?.origin === "landing" ? draft : null;
  }, []);

  const subscriptionQuery = trpc.payment.getSubscription.useQuery(undefined, {
    enabled: isAuthenticated,
  });
  const paidStatusLoading =
    isAuthenticated &&
    subscriptionQuery.data === undefined &&
    (subscriptionQuery.isLoading || subscriptionQuery.isFetching);
  const isPaidUser =
    isAuthenticated &&
    subscriptionQuery.data?.status === "active" &&
    getPlanTierIndex(subscriptionQuery.data?.plan || "free") >= 1;

  const createProject = trpc.director.createProject.useMutation();
  const { mutateAsync: classifyReferenceAsync } =
    trpc.director.classifyReferenceImage.useMutation();
  const uploadAsset = trpc.upload.uploadAiMusicVideoAsset.useMutation();
  const extractAudioFromLink = trpc.upload.extractAudioFromLink.useMutation();
  const sunoRecovery = useSunoImportRecovery();
  const historyQuery = trpc.music.getHistory.useQuery(undefined, {
    enabled: !!user,
  });
  const trpcUtils = trpc.useUtils();

  /* ---- state ---- */
  const [prompt, setPrompt] = useState(() => initialDraft?.prompt ?? "");
  const [source, setSource] = useState<SourceState>(() => {
    const s = initialDraft?.source;
    if (!s) return null;
    return {
      ...s,
      icon:
        s.icon ??
        (s.type === "upload" ? "upload" : s.type === "mine" ? "music" : "link"),
    };
  });
  const [linkValue, setLinkValue] = useState("");
  const [aspectRatio, setAspectRatio] = useState(
    () => initialDraft?.aspectRatio ?? "16:9",
  );
  const [resolution, setResolution] = useState(
    () => initialDraft?.resolution ?? "720p",
  );
  const resolutionTouchedRef = useRef(!!initialDraft?.resolution);
  // Free (and signed-out) users default to 480p — same rule as Landing.tsx.
  useEffect(() => {
    if (resolutionTouchedRef.current) return;
    if (paidStatusLoading || isPaidUser) return;
    setResolution((r) => (r === "720p" ? "480p" : r));
  }, [isPaidUser, paidStatusLoading]);
  const [isPublic, setIsPublic] = useState(() => initialDraft?.isPublic ?? true);
  const [captionStyle, setCaptionStyle] = useState<CaptionStyleId>(() =>
    normalizeCaptionStyleId(initialDraft?.captionStyle),
  );
  const [captionConfig, setCaptionConfig] = useState<CaptionConfig | null>(
    () => normalizeCaptionConfig((initialDraft as any)?.captionConfig),
  );
  const [captionModalOpen, setCaptionModalOpen] = useState(false);
  const [references, setReferences] = useState<ReferenceSelections>(() =>
    initialDraft?.references?.length
      ? { references: initialDraft.references.map((r) => ({ ...r })) }
      : {},
  );
  // Latest selection for async upload callbacks (the multi-person warning
  // needs how many slots are still free when the classifier answers).
  const referencesRef = useRef(references);
  useEffect(() => {
    referencesRef.current = references;
  }, [references]);
  const [referencePickerOpen, setReferencePickerOpen] = useState(false);
  const [menu, setMenu] = useState<MenuId | null>(null);
  const linkInputRef = useRef<HTMLTextAreaElement | null>(null);
  // Prompt + "@imageN" photo tags in the ONE field the design has: before a
  // song is attached the field takes a link OR a prompt (non-link text is the
  // prompt and moves into `prompt` when a song lands); after attaching, the
  // song pill and a prompt input share the row. Every reference chip carries
  // a stable handle so the prompt can say how that exact photo is used
  // (shared/promptImageTags.ts).
  useEffect(() => {
    const list = references.references ?? [];
    if (list.length === 0) return;
    const tagged = assignPromptImageTags(list);
    if (tagged.some((item, index) => item !== list[index])) {
      setReferences({ references: tagged });
    }
  }, [references]);
  const promptImageTagOptions = useMemo<PromptImageTagOption[]>(() => {
    if (mode === "song") return [];
    const list = assignPromptImageTags(references.references ?? []);
    const leadIndex = list.findIndex((r) => r.role === "character");
    return list.map((r, index) => ({
      tag: r.tag ?? `image${index + 1}`,
      imageUrl: r.imageUrl,
      name: r.name,
      role: r.role,
      isLead: index === leadIndex,
    }));
  }, [mode, references]);
  const fieldIsPrompt = mode === "song" || !!source;
  const promptTagMenu = usePromptImageTagMenu({
    textareaRef: linkInputRef,
    value: fieldIsPrompt ? prompt : linkValue,
    onChange: fieldIsPrompt ? setPrompt : setLinkValue,
    options: promptImageTagOptions,
    palette: {
      panel: "var(--raised)",
      border: "var(--hair)",
      fg: "var(--ink)",
      dim: "var(--ink-3)",
      accentSoft: "var(--ctrl)",
      accentBorder: "var(--accent)",
      mono: '"DM Mono", ui-monospace, monospace',
    },
  });
  // Text typed into the field before a song was attached that is not a link
  // is the user's prompt: carry it over when the song lands.
  useEffect(() => {
    if (!source || mode === "song") return;
    const typed = linkValue.trim();
    if (!typed || looksLikeLink(typed)) return;
    // What the user just typed wins over a prompt restored from an old draft.
    setPrompt(typed);
    setLinkValue("");
  }, [source, mode, linkValue]);
  const [previewSongId, setPreviewSongId] = useState<string | null>(null);
  const previewAudioRef = useRef<HTMLAudioElement | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [createAfterAudioUpload, setCreateAfterAudioUpload] = useState(false);
  const [alert, setAlert] = useState<string | null>(null);
  const [upgradeOpen, setUpgradeOpen] = useState(false);
  const [upgradeDesc, setUpgradeDesc] = useState<string | undefined>();
  const [confirmCopyrightOpen, setConfirmCopyrightOpen] = useState(false);
  const [pendingAudioFile, setPendingAudioFile] =
    useState<VideoComposerPendingFile | null>(
      () => initialDraft?.pendingAudioFile ?? null,
    );
  const [pendingReferenceFiles, setPendingReferenceFiles] = useState<
    VideoComposerPendingFile[]
  >(() => initialDraft?.pendingReferenceFiles ?? []);
  const [uploadingAudio, setUploadingAudio] = useState(false);
  const [uploadingPendingAudio, setUploadingPendingAudio] = useState(false);
  const [audioUploadProgress, setAudioUploadProgress] = useState<number | null>(
    null,
  );
  const pendingAudioUploadStartedRef = useRef<string | null>(null);
  const pendingReferenceUploadStartedRef = useRef<Set<string>>(new Set());
  const copyrightAudioOriginRef = useRef<AudioOrigin>("unknown");
  const copyrightTriggerRef = useRef("make_music_video_button");

  const rootRef = useRef<HTMLDivElement | null>(null);
  const uploadInputRef = useRef<HTMLInputElement | null>(null);

  const extracting = extractAudioFromLink.isPending;
  const audioUploadInProgress = uploadingAudio || uploadingPendingAudio;
  const sourceAwaitingAudioUpload = Boolean(
    source && !source.audioUrl && pendingAudioFile,
  );
  const ctaDisabled = submitting || extracting;
  const ctaMuted =
    ctaDisabled || audioUploadInProgress || sourceAwaitingAudioUpload;
  const ctaLabel = submitting
    ? t("landingV2.composer.creating")
    : extracting
      ? t("landingV2.composer.fetching")
      : audioUploadInProgress || sourceAwaitingAudioUpload
        ? audioUploadProgress !== null
          ? t("landingV2.composer.uploadingPct", { pct: audioUploadProgress })
          : t("landingV2.composer.uploadingAudio")
        : mode === "lyrics"
          ? t("landingV2.composer.createLyric")
          : mode === "singing"
            ? t("landingV2.composer.makeSinging")
            : mode === "dance"
              ? t("landingV2.composer.makeDance", {
                  defaultValue: "Make dance video",
                })
              : mode === "song"
                ? t("landingV2.composer.createSong")
                : t("landingV2.cta.music");

  const mySongs = useMemo(() => {
    const items = (historyQuery.data as LibrarySong[] | undefined) || [];
    return items
      .filter((item) => item.status === "completed" && !!item.audioUrl)
      .slice(0, 20);
  }, [historyQuery.data]);

  useEffect(() => {
    onSourceChange?.(source?.label ?? null);
  }, [source?.label, onSourceChange]);

  /* ---- draft persistence (restored after the sign-in reload) ---- */
  const currentDraft = useCallback(
    (overrides: Partial<VideoComposerDraft> = {}): VideoComposerDraft => ({
      origin: "landing",
      prompt,
      source: source
        ? {
            id: source.id,
            type: source.type,
            label: source.label,
            icon: source.icon,
            meta: source.meta,
            linkPlatform: source.linkPlatform,
            prompt: source.prompt,
            style: source.style,
            gender: source.gender,
            voiceGender: source.voiceGender,
            vocalGender: source.vocalGender,
            audioUrl: source.audioUrl,
          }
        : null,
      audioUrl: source?.audioUrl,
      audioFileName: source?.label,
      aspectRatio,
      resolution,
      outputType: mode,
      projectMode: PROJECT_MODE,
      captionStyle,
      captionConfig: captionConfig ?? undefined,
      isPublic,
      references: references.references?.map((r) => ({
        imageUrl: r.imageUrl,
        name: r.name,
        role: r.role,
        tag: r.tag,
        savedCharacterProjectId: r.savedCharacterProjectId,
      })),
      pendingAudioFile,
      pendingReferenceFiles,
      ...overrides,
    }),
    [
      prompt,
      source,
      aspectRatio,
      resolution,
      mode,
      captionStyle,
      captionConfig,
      isPublic,
      references,
      pendingAudioFile,
      pendingReferenceFiles,
    ],
  );
  const persistCurrentDraft = useCallback(
    (overrides: Partial<VideoComposerDraft> = {}) => {
      const draft = currentDraft(overrides);
      if (hasVideoComposerDraftInput(draft)) saveVideoComposerDraft(draft);
      else void clearVideoComposerDraft();
    },
    [currentDraft],
  );
  useEffect(() => {
    persistCurrentDraft();
  }, [persistCurrentDraft]);

  const discardPendingAudioDraft = useCallback(() => {
    const pending = pendingAudioFile;
    if (!pending) return;
    setPendingAudioFile(null);
    pendingAudioUploadStartedRef.current = null;
    void deletePendingVideoComposerFile(pending.key);
  }, [pendingAudioFile]);

  /* ---- uploads ---- */
  const onAudioProgress = (p: UploadProgress) =>
    setAudioUploadProgress(
      Math.max(0, Math.min(100, Math.round(p.percent))),
    );
  const uploadAudioToS3 = useCallback(async (file: File) => {
    const result = await uploadFileDirect(file, {
      kind: "ai-music-video",
      assetType: "audio",
      onProgress: onAudioProgress,
    });
    return result.url;
  }, []);
  const uploadImageToS3 = useCallback(
    async (file: File) => {
      const base64 = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve((reader.result as string).split(",")[1]);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
      const { url } = await uploadAsset.mutateAsync({
        fileName: file.name,
        fileData: base64,
        contentType: file.type,
        assetType: "image",
      });
      return url;
    },
    [uploadAsset],
  );

  // A file picked while signed out is parked in IndexedDB and uploaded once
  // the sign-in reload brings the user back here.
  useEffect(() => {
    const pending = pendingAudioFile;
    if (!user || !pending || pendingAudioUploadStartedRef.current === pending.key)
      return;
    pendingAudioUploadStartedRef.current = pending.key;
    let cancelled = false;
    (async () => {
      setUploadingPendingAudio(true);
      setAudioUploadProgress(0);
      try {
        const file = await loadPendingVideoComposerFile(pending.key);
        if (!file) {
          pendingAudioUploadStartedRef.current = null;
          setPendingAudioFile(null);
          setSource((prev) =>
            prev?.type === "upload" && !prev.audioUrl ? null : prev,
          );
          return;
        }
        if (file.size > MAX_AUDIO_UPLOAD_BYTES) {
          throw new Error(
            t("landingV2.composer.tooLarge", { mb: MAX_AUDIO_UPLOAD_MB }),
          );
        }
        const sizeMb = (file.size / (1024 * 1024)).toFixed(1);
        toast.info(t("landingV2.composer.uploadingAudio"));
        const url = await uploadAudioToS3(file);
        if (cancelled) return;
        setSource((prev) => ({
          type: "upload",
          label: prev?.type === "upload" ? prev.label : file.name,
          icon: "upload",
          meta: `${sizeMb}MB`,
          audioUrl: url,
        }));
        setPendingAudioFile(null);
        pendingAudioUploadStartedRef.current = null;
        await deletePendingVideoComposerFile(pending.key);
        toast.success(t("landingV2.composer.uploaded"));
      } catch (err: any) {
        if (cancelled) return;
        pendingAudioUploadStartedRef.current = null;
        setPendingAudioFile(null);
        setCreateAfterAudioUpload(false);
        setSource((prev) =>
          prev?.type === "upload" && !prev.audioUrl ? null : prev,
        );
        void deletePendingVideoComposerFile(pending.key);
        toast.error(err?.message || t("landingV2.composer.uploadFailed"));
      } finally {
        if (!cancelled) {
          setUploadingPendingAudio(false);
          setAudioUploadProgress(null);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
    // Scoped to the upload identity on purpose (see Landing.tsx).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingAudioFile?.key, user?.id]);

  useEffect(() => {
    if (!user || pendingReferenceFiles.length === 0) return;
    let cancelled = false;
    (async () => {
      for (const pending of pendingReferenceFiles) {
        if (pendingReferenceUploadStartedRef.current.has(pending.key)) continue;
        pendingReferenceUploadStartedRef.current.add(pending.key);
        try {
          const file = await loadPendingVideoComposerFile(pending.key);
          if (!file || cancelled) {
            setPendingReferenceFiles((prev) =>
              prev.filter((item) => item.key !== pending.key),
            );
            continue;
          }
          const url = await uploadImageToS3(file);
          if (cancelled) return;
          setReferences((prev) => ({
            references: [
              ...(prev.references ?? []),
              {
                imageUrl: url,
                name: file.name.replace(/\.[^.]+$/, "") || t("landingV2.composer.reference"),
                role: "character" as const,
              },
            ].slice(0, MAX_REFERENCES),
          }));
          // Photos picked while signed out never went through the picker's
          // classifier: a group photo must still warn that only one person
          // will be cast.
          void classifyReferenceAsync({ imageUrl: url })
            .then(({ subjectCount }) => {
              setReferences((prev) => ({
                references: (prev.references ?? []).map((r) =>
                  r.imageUrl === url
                    ? { ...r, subjectCount: subjectCount ?? null }
                    : r,
                ),
              }));
              const selected = referencesRef.current.references ?? [];
              if (!selected.some((r) => r.imageUrl === url)) return;
              toastMultiPersonCastPhoto({
                subjectCount,
                freeSlots: MAX_REFERENCES - selected.length,
                surface: "web_landing_pending_upload",
              });
            })
            .catch(() => {});
          setPendingReferenceFiles((prev) =>
            prev.filter((item) => item.key !== pending.key),
          );
          await deletePendingVideoComposerFile(pending.key);
        } catch (err: any) {
          pendingReferenceUploadStartedRef.current.delete(pending.key);
          if (!cancelled) toast.error(err?.message || t("landingV2.composer.uploadFailed"));
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [classifyReferenceAsync, pendingReferenceFiles, uploadImageToS3, user]);

  /* ---- song sources ---- */
  const stopSongPreview = () => {
    previewAudioRef.current?.pause();
    setPreviewSongId(null);
  };
  const toggleSongPreview = (song: LibrarySong) => {
    if (!song.audioUrl) return;
    if (previewSongId === song.id) {
      stopSongPreview();
      return;
    }
    let audio = previewAudioRef.current;
    if (!audio) {
      audio = new Audio();
      audio.onended = () => setPreviewSongId(null);
      previewAudioRef.current = audio;
    }
    audio.src = song.audioUrl;
    void audio.play().catch(() => setPreviewSongId(null));
    setPreviewSongId(song.id);
  };
  useEffect(() => {
    onDockSongOpenChange?.(menu === "docksong");
  }, [menu, onDockSongOpenChange]);
  useEffect(() => {
    if (menu !== "song" && menu !== "docksong") stopSongPreview();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [menu]);
  useEffect(() => () => previewAudioRef.current?.pause(), []);

  const attachSong = (song: LibrarySong) => {
    discardPendingAudioDraft();
    setSource({
      id: song.id,
      type: "mine",
      label: song.title || t("landingV2.composer.untitled"),
      icon: "music",
      meta: song.genre || undefined,
      prompt: song.prompt,
      style: song.style,
      gender: song.gender,
      voiceGender: song.voiceGender,
      vocalGender: song.vocalGender,
      audioUrl: song.audioUrl || undefined,
    });
    setMenu(null);
  };

  const handleUploadPicked = async (file: File | null) => {
    if (!file) return;
    sunoRecovery.clear();
    // Photos, PDFs, lyric sheets and MIDI used to upload fine and only fail
    // ~80 s into music analysis, after the project existed.
    if (!isAcceptedAudioSourceFile(file)) {
      toast.error(t("landingV2.composer.fileTypes", { mb: MAX_AUDIO_UPLOAD_MB }));
      return;
    }
    if (file.size > MAX_AUDIO_UPLOAD_BYTES) {
      toast.error(t("landingV2.composer.tooLarge", { mb: MAX_AUDIO_UPLOAD_MB }));
      return;
    }
    setMenu(null);
    if (!user) {
      try {
        const pending = await savePendingVideoComposerFile("landing-audio", file);
        const sizeMb = (file.size / (1024 * 1024)).toFixed(1);
        const pendingSource: SourceState = {
          type: "upload",
          label: file.name,
          icon: "upload",
          meta: `${sizeMb}MB`,
        };
        setPendingAudioFile(pending);
        setSource(pendingSource);
        persistCurrentDraft({
          source: pendingSource,
          audioFileName: file.name,
          pendingAudioFile: pending,
        });
      } catch (err: any) {
        toast.error(
          err?.message ||
            t("landingV2.composer.couldNotSave"),
        );
        return;
      }
      onRequireAuth("song_upload");
      return;
    }
    try {
      discardPendingAudioDraft();
      setUploadingAudio(true);
      setAudioUploadProgress(0);
      toast.info(t("landingV2.composer.uploadingAudio"));
      const s3Url = await uploadAudioToS3(file);
      const sizeMb = (file.size / (1024 * 1024)).toFixed(1);
      setSource({
        type: "upload",
        label: file.name,
        icon: "upload",
        meta: `${sizeMb}MB`,
        audioUrl: s3Url,
      });
      toast.success(t("landingV2.composer.uploaded"));
    } catch (err: any) {
      toast.error(err?.message || t("landingV2.composer.uploadFailed"));
    } finally {
      setUploadingAudio(false);
      setAudioUploadProgress(null);
    }
  };

  // Links that can never yield audio: truncated YouTube URLs, Spotify
  // previews, streaming/AI-music song pages and social posts. Run on attach
  // and again before creating, since a raw link can also come back from a
  // saved draft. This composer had no page check, so it was the main source
  // of "web page instead of audio" analysis failures (Mureka, Udio,
  // Instagram, …) after Sep 21 2026.
  const songLinkRejection = (url: string): string | null =>
    getYouTubeLinkError(url) ??
    (isSpotifyUrl(url) ? t("landingV2.composer.spotify") : null) ??
    getUnsupportedAudioPageError(url);
  const needsLinkImport = (url: string) =>
    isYouTubeUrl(url) || isTikTokUrl(url) || isSunoAudioUrl(url);

  const attachLink = async (retryUrl?: string): Promise<SourceState> => {
    const raw = retryUrl ?? linkValue.trim();
    if (!raw) return null;
    const withScheme = raw.startsWith("http") ? raw : `https://${raw}`;
    let parsed: URL;
    try {
      parsed = new URL(withScheme);
    } catch {
      toast.error(t("landingV2.composer.invalidLink"));
      return null;
    }
    if (isEmailAddressLink(withScheme)) {
      toast.error(t("landingV2.composer.invalidLink"));
      return null;
    }
    const linkRejection = songLinkRejection(withScheme);
    if (linkRejection) {
      toast.error(linkRejection);
      return null;
    }
    const prettyLabel =
      parsed.hostname.replace(/^www\./, "") +
      parsed.pathname.replace(/\/$/, "").slice(0, 18);

    if (needsLinkImport(withScheme)) {
      const platform = isSunoAudioUrl(withScheme) ? "Suno" : isYouTubeUrl(withScheme) ? "YouTube" : "TikTok";
      const linkPlatform = platform === "Suno" ? "suno" : platform === "YouTube" ? "youtube" : "tiktok";
      if (!user) {
        const pendingSource: SourceState = {
          type: "link",
          label: prettyLabel || `${platform} link`,
          icon: "link",
          meta: platform,
          linkPlatform,
          audioUrl: withScheme,
        };
        setSource(pendingSource);
        setLinkValue("");
        persistCurrentDraft({ source: pendingSource, audioUrl: withScheme });
        onRequireAuth("song_link");
        return null;
      }
      try {
        toast.info(t("landingV2.composer.extracting", { platform }));
        const result = await sunoRecovery.run(withScheme, () => extractAudioFromLink.mutateAsync({ url: withScheme }));
        const nextSource: SourceState = {
          type: "link",
          label: result.title || `${platform} track`,
          icon: "link",
          meta: platform,
          linkPlatform,
          audioUrl: result.url,
        };
        discardPendingAudioDraft();
        setSource(nextSource);
        setLinkValue("");
        toast.success(t("landingV2.composer.attachedFrom", { platform }));
        return nextSource;
      } catch (err: any) {
        toast.error(err?.message || t("landingV2.composer.couldNotExtract"));
        return null;
      }
    }
    const nextSource: SourceState = {
      type: "link",
      label: prettyLabel,
      icon: "link",
      linkPlatform: isSunoAudioUrl(withScheme) ? "suno" : "other",
      audioUrl: withScheme,
    };
    discardPendingAudioDraft();
    setSource(nextSource);
    setLinkValue("");
    return nextSource;
  };

  const clearSource = () => {
    setSource(null);
    discardPendingAudioDraft();
  };

  /* ---- options ---- */
  const privateProjectUpgradeDescription = t("landingV2.composer.privatePaid");
  const handleVisibilityToggle = () => {
    if (isPublic && !isPaidUser) {
      if (paidStatusLoading) return;
      setUpgradeDesc(privateProjectUpgradeDescription);
      setUpgradeOpen(true);
      return;
    }
    setIsPublic((v) => !v);
  };
  const pickResolution = (opt: (typeof RESOLUTIONS)[number]) => {
    if (opt.paidOnly && !isPaidUser && !paidStatusLoading) {
      setMenu(null);
      setUpgradeDesc(
        t("landingV2.composer.upgrade1080"),
      );
      setUpgradeOpen(true);
      return;
    }
    resolutionTouchedRef.current = true;
    setResolution(opt.id);
    setMenu(null);
  };

  /* ---- create ---- */
  const normalizedAspectRatio = aspectRatio === "9:16" ? "9:16" : "16:9";
  const normalizedResolution =
    resolution === "1080p" ? "1080p" : resolution === "480p" ? "480p" : "720p";

  const createInFlight = useRef(false);
  const createFromSource = async (sourceOverride?: SourceState) => {
    let src = sourceOverride ?? source;
    if (!src) {
      setAlert(t("landingV2.composer.attachFirst"));
      linkInputRef.current?.focus();
      return;
    }
    setAlert(null);
    // A link restored from a sign-in draft is still the raw page link: import
    // it (Suno, YouTube, TikTok) or re-check it before the project exists.
    // Restored YouTube links used to go straight to createProject and fail in
    // analysis (Sep 21–29 2026: 11 landing projects).
    if (src.type === "link" && src.audioUrl && user) {
      if (needsLinkImport(src.audioUrl)) {
        if (extractAudioFromLink.isPending || submitting) return;
        const imported = await attachLink(src.audioUrl);
        if (!imported) return;
        src = imported;
      } else {
        const linkRejection = isEmailAddressLink(src.audioUrl)
          ? t("landingV2.composer.invalidLink")
          : songLinkRejection(src.audioUrl);
        if (linkRejection) {
          toast.error(linkRejection);
          return;
        }
      }
    }
    if (mode === "singing" && src.audioUrl) {
      persistCurrentDraft({
        outputType: "singing",
        source: src,
        audioUrl: src.audioUrl,
        aspectRatio: normalizedAspectRatio,
        resolution:
          normalizedResolution === "1080p" ? "720p" : normalizedResolution,
      });
      navigate("/ai-singing-video-generator");
      return;
    }
    if (mode === "lyrics" && src.audioUrl) {
      persistCurrentDraft({
        outputType: "lyrics",
        source: src,
        audioUrl: src.audioUrl,
        aspectRatio: normalizedAspectRatio,
        resolution:
          normalizedResolution === "480p" ? "720p" : normalizedResolution,
      });
      navigate("/lyrics-video-generator");
      return;
    }
    if (!user) {
      persistCurrentDraft();
      onRequireAuth("make_button");
      return;
    }
    if (!src.audioUrl) {
      toast.error(
        src.type === "mine"
          ? t("landingV2.composer.noAudio")
          : t("landingV2.composer.processing"),
      );
      return;
    }
    setSubmitting(true);
    try {
      const refs = assignPromptImageTags(references.references ?? []);
      const chars = refs.filter((r) => r.role === "character");
      const vibe = refs.find((r) => r.role === "vibe");
      const metadataVoiceGender =
        normalizeVoiceGender(src.voiceGender) ||
        normalizeVoiceGender(src.vocalGender) ||
        normalizeVoiceGender(src.gender);
      const vocalPresentation =
        vocalPresentationFromValue(metadataVoiceGender, "song_metadata", 1) ||
        inferVocalPresentationFromText(
          [src.style, src.meta, src.prompt, prompt, src.label]
            .filter(Boolean)
            .join(" "),
        );
      const inferredVoiceGender = vocalPresentation?.value;
      const intentParts = [prompt.trim()].filter(Boolean);
      if (mode === "dance") intentParts.push(DANCE_INTENT);
      if (inferredVoiceGender) {
        intentParts.push(`Voice presentation: ${inferredVoiceGender}.`);
      }
      const hasUploadedCharacter = chars.some(
        (c) => !isStockCharacterPresetUrl(c.imageUrl),
      );
      if (mode === "dance" && hasUploadedCharacter) {
        intentParts.push(DANCE_LOCK_LOOK);
      }
      const composedIntent = intentParts.join(" ");
      trackVideoProjectInitiated({
        source: "landing_page",
        aspect_ratio: aspectRatio,
        resolution,
        project_mode: projectModeFor(mode, user?.directorDefaultArm),
        director_default_arm: user?.directorDefaultArm ?? undefined,
        has_audio: true,
        has_character_image: chars.length > 0,
        has_vibe_image: !!vibe?.imageUrl,
      });
      const { projectId } = await createProject.mutateAsync({
        title: src.label || t("landingV2.composer.untitledProject"),
        audioUrl: src.audioUrl,
        characterImageUrl: chars[0]?.imageUrl,
        // Saved character as the lead: the server copies that project's
        // finished cards + identity text instead of minting new ones.
        savedCharacterSourceProjectId: chars[0]?.savedCharacterProjectId,
        additionalCharacterImageUrls: chars
          .slice(1)
          .map((c) => c.imageUrl)
          .filter(Boolean),
        vibeImageUrl: vibe?.imageUrl,
        // "@imageN" handles per chip — the server binds each to the slot
        // its photo landed in so "@image2 is my wife" means that photo.
        promptImageTags: refs
          .filter((r) => !!r.tag)
          .map((r) => ({ tag: r.tag as string, imageUrl: r.imageUrl })),
        aspectRatio: normalizedAspectRatio,
        resolution: normalizedResolution,
        voiceGender: inferredVoiceGender,
        vocalPresentation,
        projectMode: projectModeFor(mode, user?.directorDefaultArm),
        ...(mode === "dance" && hasUploadedCharacter
          ? { lockCharacterLook: true }
          : {}),
        isPublic,
        captionStyle:
          captionConfig && captionStyle !== "none" ? captionStyle : undefined,
        captionConfig: captionConfig ?? undefined,
        initialPrompt: composedIntent || undefined,
      });
      void trpcUtils.director.listProjects.invalidate();
      try {
        if (composedIntent) {
          sessionStorage.setItem(
            `director_initial_intent_${projectId}`,
            composedIntent,
          );
        }
        sessionStorage.setItem(
          `director_render_prefs_${projectId}`,
          JSON.stringify({
            aspectRatio,
            resolution,
            projectMode: projectModeFor(mode, user?.directorDefaultArm),
          }),
        );
      } catch {}
      void clearVideoComposerDraft();
      navigate(`/ai-music-video/${projectId}`);
    } catch (err: any) {
      const msg = String(err?.message || t("landingV2.composer.couldNotStart"));
      if (/insufficient credits/i.test(msg)) {
        setUpgradeDesc(msg);
        setUpgradeOpen(true);
      } else if (/private projects|active paid subscription/i.test(msg)) {
        setUpgradeDesc(privateProjectUpgradeDescription);
        setUpgradeOpen(true);
      } else {
        toast.error(msg);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleCreate = async (sourceOverride?: SourceState) => {
    if (createInFlight.current) return;
    createInFlight.current = true;
    try {
      await createFromSource(sourceOverride);
    } finally {
      createInFlight.current = false;
    }
  };

  const copyrightPayload = (trigger = copyrightTriggerRef.current) => ({
    source: "landing_page",
    trigger,
    output_type:
      mode === "lyrics"
        ? "lyrics_video"
        : mode === "singing"
          ? "singing_video"
          : mode === "dance"
            ? "dance_video"
            : "music_video",
    project_mode: projectModeFor(mode, user?.directorDefaultArm),
    aspect_ratio: aspectRatio,
    resolution,
    is_public: isPublic,
    audio_origin: copyrightAudioOriginRef.current,
  });
  const triggerName = () =>
    mode === "lyrics"
      ? "make_lyric_video_button"
      : mode === "singing"
        ? "make_singing_video_button"
        : mode === "dance"
          ? "make_dance_video_button"
          : "make_music_video_button";
  const trackTry = () => {
    const trigger = triggerName();
    if (mode === "lyrics") {
      trackTryGenerateLyricVideo({
        source: "landing_page",
        trigger,
        has_audio: true,
        aspect_ratio: aspectRatio,
        resolution,
      });
      return;
    }
    trackTryGenerateVideo({
      source: "landing_page",
      trigger,
      has_audio: true,
      ...(mode === "singing"
        ? {}
        : { project_mode: projectModeFor(mode, user?.directorDefaultArm) }),
      aspect_ratio: aspectRatio,
      resolution,
    });
  };
  // Uploads, YouTube/TikTok and unknown drafts need the copyright
  // confirmation once; Solmi songs, Suno links and demo tracks don't.
  const startVideoCreationWithConsent = (src: SourceState, trigger: string) => {
    const origin = sourceAudioOrigin(src);
    copyrightAudioOriginRef.current = origin;
    if (
      !audioOriginRequiresCopyrightConfirmation(origin) ||
      hasAcknowledgedCopyright(user?.id)
    ) {
      void handleCreate(src);
      return;
    }
    copyrightTriggerRef.current = trigger;
    trackVideoCopyrightConfirmationShown(copyrightPayload(trigger));
    setConfirmCopyrightOpen(true);
  };

  useEffect(() => {
    if (
      !createAfterAudioUpload ||
      ctaDisabled ||
      audioUploadInProgress ||
      !source?.audioUrl
    )
      return;
    setCreateAfterAudioUpload(false);
    startVideoCreationWithConsent(source, `${triggerName()}_after_upload`);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [audioUploadInProgress, createAfterAudioUpload, source?.audioUrl, ctaDisabled]);

  const submit = async () => {
    if (mode === "song") {
      const p = prompt.trim();
      if (!p) {
        setAlert(t("landingV2.composer.describeFirst"));
        linkInputRef.current?.focus();
        return;
      }
      navigate(`/ai-music-generator?prompt=${encodeURIComponent(p)}`);
      return;
    }
    if (ctaDisabled) return;
    if (!source && !looksLikeLink(linkValue.trim())) {
      // Empty, or a prompt typed ahead of the song — either way a song is
      // needed first (the prompt is kept in the field).
      setAlert(t("landingV2.composer.attachFirst"));
      linkInputRef.current?.focus();
      return;
    }
    setAlert(null);
    if (source) {
      if (!source.audioUrl) {
        if (audioUploadInProgress || sourceAwaitingAudioUpload)
          setCreateAfterAudioUpload(true);
        toast.info(t("landingV2.composer.uploadingAudio"));
        return;
      }
      trackTry();
      if (!user) {
        void handleCreate();
        return;
      }
      startVideoCreationWithConsent(source, triggerName());
      return;
    }
    const attached = await attachLink();
    if (attached?.audioUrl) {
      trackTry();
      if (!user) {
        void handleCreate(attached);
        return;
      }
      startVideoCreationWithConsent(attached, triggerName());
    }
  };

  useImperativeHandle(
    ref,
    () => ({
      submit: () => void submit(),
      openSongMenu: () => setMenu("song"),
      toggleDockSongMenu: () => setMenu((m) => (m === "docksong" ? null : "docksong")),
      // Same seed as Landing.tsx's loadDemo. Resolution is left alone: the
      // demos are 720p/1080p, which free accounts cannot render.
      loadDemo: (demo) => {
        discardPendingAudioDraft();
        sunoRecovery.clear();
        setMenu(null);
        setAlert(null);
        setLinkValue("");
        setPrompt(demo.remixPrompt);
        setSource({
          type: "link",
          label: demo.audioLabel,
          icon: "music",
          meta: t("newLanding.composer.demoMusic"),
          linkPlatform: "demo",
          audioUrl: demo.audioUrl,
        });
        setAspectRatio(demo.aspectRatio);
        setReferences({
          references: [
            {
              imageUrl: demo.primaryImageUrl,
              name: t("newLanding.composer.demoCharacter"),
              role: "character",
            },
          ],
        });
        requestAnimationFrame(() => {
          rootRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
          linkInputRef.current?.focus({ preventScroll: true });
        });
        toast.success(t("newLanding.composer.demoLoaded"));
      },
    }),
  );

  useEffect(() => {
    if (alert && (source || linkValue.trim() || prompt.trim())) setAlert(null);
  }, [alert, source, linkValue, prompt]);

  /* ---- menus: one open at a time, outside click / Escape closes ---- */
  useEffect(() => {
    if (!menu) return;
    const onDown = (e: Event) => {
      const t = e.target as Node;
      if (rootRef.current?.contains(t)) return;
      if (document.getElementById(DOCK_SONG_POP_ID)?.contains(t)) return;
      if ((t as HTMLElement).closest?.("#songBtn")) return;
      setMenu(null);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setMenu(null);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [menu]);
  const toggleMenu = (id: MenuId) => setMenu((m) => (m === id ? null : id));

  const refs = references.references ?? [];
  const showImages = mode === "video" || mode === "singing" || mode === "dance";
  const showResolution = mode !== "song";
  const showPublic = mode !== "song";
  const showAspect = mode === "video" || mode === "lyrics" || mode === "dance";
  const showCaptions = mode === "video";
  const showMore = showAspect || showCaptions;

  const [platformIndex, setPlatformIndex] = useState(0);
  useEffect(() => {
    if (mode === "song" || source || linkValue.trim() || extracting) return;
    const id = window.setInterval(
      () => setPlatformIndex((i) => (i + 1) % LINK_PLATFORMS.length),
      LINK_PLACEHOLDER_INTERVAL_MS,
    );
    return () => window.clearInterval(id);
  }, [mode, source, linkValue, extracting]);
  const fieldPlaceholder =
    mode === "song"
      ? t("landingV2.composer.describe")
      : source
        ? t("landingV2.composer.promptVideo")
        : t("landingV2.composer.placeholder", { platform: LINK_PLATFORMS[platformIndex] });
  const fieldValue = fieldIsPrompt ? prompt : linkValue;
  // Auto-grow the field with its content (one row for a link, more for a
  // pasted multi-line prompt), capped at FIELD_MAX_ROWS then scrolling.
  const fitField = useCallback(() => {
    const el = linkInputRef.current;
    if (!el) return;
    el.style.height = "auto";
    const cs = window.getComputedStyle(el);
    const line = parseFloat(cs.lineHeight) || 24;
    const pad = (parseFloat(cs.paddingTop) || 0) + (parseFloat(cs.paddingBottom) || 0);
    const max = FIELD_MAX_ROWS * line + pad;
    el.style.height = `${Math.min(el.scrollHeight, max)}px`;
    el.style.overflowY = el.scrollHeight > max ? "auto" : "hidden";
  }, []);
  useLayoutEffect(fitField, [fitField, fieldValue, source, mode]);
  // The landing stylesheet and fonts land after first paint, and the row
  // changes width on resize: re-fit whenever the field's box changes.
  useEffect(() => {
    const el = linkInputRef.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(() => fitField());
    ro.observe(el.parentElement ?? el);
    void document.fonts?.ready.then(fitField);
    return () => ro.disconnect();
  }, [fitField]);

  const songRowLabels = {
    play: t("landingV2.composer.playPreview"),
    pause: t("landingV2.composer.pausePreview"),
    untitled: t("landingV2.composer.untitled"),
  };
  const songMenuItems = (
    <>
              <button
                type="button"
                className="up"
                role="menuitem"
                onClick={() => uploadInputRef.current?.click()}
              >
                <span className="up-ic">{uploadIcon}</span>
                <span>
                  <span className="t">{t("landingV2.composer.uploadSong")}</span>
                  <span className="g">{t("landingV2.composer.fileTypes", { mb: MAX_AUDIO_UPLOAD_MB })}</span>
                </span>
              </button>

              <div className="lbl">{t("landingV2.composer.samples")}</div>
              {LANDING_SAMPLE_SONGS.map((song) => (
                <SongRow
                  key={song.id}
                  song={song}
                  playing={previewSongId === song.id}
                  onPlay={() => toggleSongPreview(song)}
                  onPick={() => attachSong(song)}
                  labels={songRowLabels}
                />
              ))}

              <button
                type="button"
                className="create"
                role="menuitem"
                onClick={() => {
                  setMenu(null);
                  navigate("/ai-music-generator");
                }}
              >
                <span>{noteIconSm} {t("landingV2.composer.createSong")}</span>
                {arrowIcon}
              </button>

              <div className="lbl">{t("landingV2.composer.mySongs")}</div>
              {!user ? (
                <div className="note">{t("landingV2.composer.signInSongs")}</div>
              ) : historyQuery.isLoading ? (
                <div className="note">{t("landingV2.composer.loading")}</div>
              ) : mySongs.length === 0 ? (
                <div className="note">{t("landingV2.composer.noSongs")}</div>
              ) : (
                <div className="mine">
                  {mySongs.map((song) => (
                    <SongRow
                      key={song.id}
                      song={song}
                      playing={previewSongId === song.id}
                      onPlay={() => toggleSongPreview(song)}
                      onPick={() => attachSong(song)}
                      labels={songRowLabels}
                    />
                  ))}
                </div>
              )}
    </>
  );
  const dockPop = typeof document !== "undefined" ? document.getElementById(DOCK_SONG_POP_ID) : null;

  // The pill splits into "paste a link" OR "select a song" only while
  // nothing is attached; once a source exists the field becomes the prompt.
  const splitField = !source && mode !== "song";
  // Once a track is attached (outside song mode) the block stacks: track row
  // on top, optional description row underneath.
  const stackedField = !!source && mode !== "song";
  const fieldTextarea = (
    <textarea
      ref={linkInputRef}
      rows={1}
      value={fieldValue}
      onChange={(e) =>
        fieldIsPrompt ? setPrompt(e.target.value) : setLinkValue(e.target.value)
      }
      onKeyDown={(e) => {
        promptTagMenu.textareaProps.onKeyDown(e);
        if (e.defaultPrevented) return;
        // Enter submits like the single-line field always did;
        // Shift+Enter is the way to type a line break by hand.
        if (e.key === "Enter" && !e.shiftKey && !e.nativeEvent.isComposing) {
          e.preventDefault();
          void submit();
        }
      }}
      onKeyUp={promptTagMenu.textareaProps.onKeyUp}
      onClick={promptTagMenu.textareaProps.onClick}
      onBlur={promptTagMenu.textareaProps.onBlur}
      disabled={extracting}
      placeholder={fieldPlaceholder}
      aria-label={fieldPlaceholder}
    />
  );

  return (
    <div
      className="composer"
      data-enter=""
      style={{ "--d": enterDelay } as React.CSSProperties}
      ref={rootRef}
    >
      {sunoRecovery.failure && <SunoImportRecovery
        failure={sunoRecovery.failure}
        busy={extracting || audioUploadInProgress}
        onUpload={() => uploadInputRef.current?.click()}
        onRetry={() => {
          const url = sunoRecovery.failure!.url;
          sunoRecovery.allowRetry(url);
          void attachLink(url);
        }}
      />}
      <div className="c-top-wrap">
        <div className={"c-top" + (stackedField ? " stacked" : "")}>
          {splitField ? (
            /* Empty state: one pill, two equal ways in. The link half and
               the "Select a song" half read as alternatives (an OR sits on
               the divider); typing a link dims the other half. */
            <div
              className="c-field c-split"
              data-typing={linkValue.trim() ? "true" : "false"}
            >
              <label className="c-half c-half-link">
                {linkIcon}
                {fieldTextarea}
              </label>
              <span className="c-or" aria-hidden="true">
                <span>{t("landingV2.composer.or")}</span>
              </span>
              <div
                className="c-half c-half-song drop"
                data-open={menu === "song"}
              >
                <button
                  type="button"
                  className="c-pick"
                  aria-haspopup="true"
                  aria-expanded={menu === "song"}
                  onClick={() => toggleMenu("song")}
                >
                  {noteIconSm}
                  <span className="c-pick-t">
                    {t("landingV2.composer.selectSong")}
                  </span>
                  {chevron}
                </button>
                <div className="menu songs" role="menu">
                  {songMenuItems}
                </div>
              </div>
            </div>
          ) : stackedField && source ? (
            /* Attached: the track stays on top as a removable pill and the
               optional description gets its own row underneath, in the same
               field block as the empty-state split pill. */
            <div className="c-field c-stack">
              <span className="src" title={source.label}>
                {sourceIcon(source.icon)}
                <span className="src-t">{source.label}</span>
                <button
                  type="button"
                  className="src-x"
                  aria-label={t("landingV2.composer.removeSong")}
                  onClick={clearSource}
                >
                  {xIcon}
                </button>
              </span>
              <label className="c-desc">
                {fieldTextarea}
                <span className="c-opt" aria-hidden="true">
                  {t("landingV2.composer.optional")}
                </span>
              </label>
            </div>
          ) : (
            <label className="c-field">
              {noteIcon}
              {fieldTextarea}
            </label>
          )}
          <button
            className={"cta" + (ctaMuted ? " muted" : "")}
            onClick={() => void submit()}
            disabled={ctaDisabled}
          >
            <span className="lbl">{ctaLabel}</span>
          </button>
        </div>
        {promptTagMenu.menu}
        {/* One dim line once a photo is attached and the prompt has no
            @-tag yet; the component hides itself as soon as one is typed. */}
        <PromptImageTagHint
          className="c-tag-hint"
          options={promptImageTagOptions}
          prompt={fieldValue}
          style={{ color: "var(--ink-3)" }}
        />
      </div>
      {alert && (
        <div className="c-alert" role="alert">
          {alert}
        </div>
      )}

      {mode !== "song" && (
        <div className="c-bot">

          {showImages && (
            <button
              type="button"
              className={"chip" + (refs.length ? " on" : "")}
              onClick={() => setReferencePickerOpen(true)}
              title={t("landingV2.composer.imagesTitle")}
            >
              {refs.length ? (
                <span className="avs">
                  {refs.slice(0, 3).map((r, i) => (
                    <img key={i} src={r.imageUrl} alt={r.name} />
                  ))}
                </span>
              ) : (
                imagesIcon
              )}
              {refs.length
                ? refs.length === 1
                  ? t("landingV2.composer.imagesOne")
                  : t("landingV2.composer.imagesCount", { count: refs.length })
                : t("landingV2.composer.images")}
            </button>
          )}

          {showImages && <LeadCharacterSelect selected={references} onChange={setReferences} />}

          {showResolution && (
            <div className="drop" data-open={menu === "res"}>
              <button
                type="button"
                className="chip quiet"
                aria-haspopup="true"
                aria-expanded={menu === "res"}
                onClick={() => toggleMenu("res")}
                title={t("landingV2.composer.resolutionTitle")}
              >
                {resolution}
                {chevron}
              </button>
              <div className="menu small" role="menu">
                {RESOLUTIONS.map((opt) => (
                  <button
                    key={opt.id}
                    type="button"
                    role="menuitemradio"
                    aria-checked={resolution === opt.id}
                    onClick={() => pickResolution(opt)}
                  >
                    {opt.label}
                    {opt.paidOnly && !isPaidUser && <span>{lockIcon}</span>}
                  </button>
                ))}
              </div>
            </div>
          )}

          {(showPublic || showMore) && <span className="sep" aria-hidden="true"></span>}

          {showPublic && (
            <button
              type="button"
              className="chip quiet icon-only"
              aria-pressed={isPublic}
              aria-label={isPublic ? t("landingV2.composer.publicProject") : t("landingV2.composer.privateProject")}
              title={
                isPublic
                  ? t("landingV2.composer.publicTitle")
                  : t("landingV2.composer.privateTitle")
              }
              onClick={handleVisibilityToggle}
            >
              {isPublic ? globeIcon : lockIcon}
            </button>
          )}

          {showMore && (
            <div className="drop right" data-open={menu === "more"}>
              <button
                type="button"
                className="chip quiet"
                aria-haspopup="true"
                aria-expanded={menu === "more"}
                onClick={() => toggleMenu("more")}
              >
                {dotsIcon}
                {t("landingV2.composer.more")}
              </button>
              <div className="menu more" role="menu">
                {showAspect && (
                  <div className="row">
                    <span className="k">{t("landingV2.composer.aspect")}</span>
                    <span className="seg" role="radiogroup" aria-label={t("landingV2.composer.aspect")}>
                      {["16:9", "9:16"].map((ar) => (
                        <button
                          key={ar}
                          type="button"
                          role="radio"
                          aria-checked={aspectRatio === ar}
                          onClick={() => setAspectRatio(ar)}
                        >
                          <AspectShape value={ar} />
                          {ar}
                        </button>
                      ))}
                    </span>
                  </div>
                )}
                {showCaptions && (
                  <div className="row">
                    <span className="k">{t("landingV2.composer.captions")}</span>
                    <span className="seg">
                      <button
                        type="button"
                        aria-pressed={!!captionConfig}
                        onClick={() => {
                          setMenu(null);
                          setCaptionModalOpen(true);
                        }}
                        title={
                          captionConfig
                            ? t("landingV2.composer.captionsOn", { label: captionConfigLabel(captionStyle, captionConfig) })
                            : t("landingV2.composer.captionsHint")
                        }
                      >
                        {captionsIcon}
                        {captionConfig
                          ? captionConfigLabel(captionStyle, captionConfig)
                          : t("landingV2.composer.captionsOff")}
                      </button>
                      {captionConfig && (
                        <button
                          type="button"
                          aria-label={t("landingV2.composer.removeCaptions")}
                          title={t("landingV2.composer.removeCaptions")}
                          onClick={() => {
                            setCaptionStyle("none");
                            setCaptionConfig(null);
                          }}
                        >
                          {xIcon}
                        </button>
                      )}
                    </span>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {menu === "docksong" && dockPop
        ? createPortal(
            <div className="menu songs static" role="menu">
              {songMenuItems}
            </div>,
            dockPop,
          )
        : null}

      <input
        ref={uploadInputRef}
        type="file"
        accept={AUDIO_UPLOAD_ACCEPT}
        hidden
        onChange={(e) => {
          const f = e.target.files?.[0] ?? null;
          void handleUploadPicked(f);
          e.target.value = "";
        }}
      />

      <ReferencePickerModal
        open={referencePickerOpen}
        onClose={() => setReferencePickerOpen(false)}
        selected={references}
        onChange={setReferences}
        onRequireAuth={() => onRequireAuth("reference_photo")}
        onUnauthenticatedFile={async (file) => {
          const pending = await savePendingVideoComposerFile(
            `landing-reference-${Date.now()}`,
            file,
          );
          const next = [...pendingReferenceFiles, pending].slice(0, 3);
          setPendingReferenceFiles(next);
          persistCurrentDraft({ pendingReferenceFiles: next });
        }}
        singleCharacter={mode === "singing"}
      />
      <LyricsStyleModal
        open={captionModalOpen}
        initialStyleId={captionStyle}
        initialConfig={captionConfig}
        onCancel={() => setCaptionModalOpen(false)}
        onConfirm={(result) => {
          setCaptionStyle(
            (result.config ? result.styleId : "none") as CaptionStyleId,
          );
          setCaptionConfig(result.config);
          setCaptionModalOpen(false);
        }}
      />
      <CopyrightConfirmDialog
        open={confirmCopyrightOpen}
        onOpenChange={(next) => {
          if (!next && confirmCopyrightOpen)
            trackVideoCopyrightConfirmationCancelled(copyrightPayload());
          setConfirmCopyrightOpen(next);
        }}
        onConfirm={() => {
          markCopyrightAcknowledged(user?.id);
          setConfirmCopyrightOpen(false);
          void handleCreate();
        }}
      />
      <UpgradeModal
        open={upgradeOpen}
        onClose={() => setUpgradeOpen(false)}
        description={upgradeDesc}
        trigger="director_pipeline"
      />
    </div>
  );
});

export default ComposerV2;

function SongRow({
  song,
  playing,
  onPlay,
  onPick,
  labels,
}: {
  song: LibrarySong;
  playing: boolean;
  onPlay: () => void;
  onPick: () => void;
  labels: { play: string; pause: string; untitled: string };
}) {
  return (
    <div className="song">
      <button
        type="button"
        className="pp"
        aria-label={playing ? labels.pause : labels.play}
        onClick={onPlay}
      >
        {playing ? pauseIcon : playIcon}
      </button>
      <button type="button" className="pick" role="menuitem" onClick={onPick}>
        <span className="t">{song.title || labels.untitled}</span>
        {song.genre && <span className="g">{song.genre}</span>}
      </button>
    </div>
  );
}

function AspectShape({ value }: { value: string }) {
  const [a, b] = value.split(":").map(Number);
  const r = a && b ? a / b : 16 / 9;
  const [w, h] = r >= 1 ? [12, Math.round(12 / r)] : [Math.round(12 * r), 12];
  return (
    <span
      aria-hidden="true"
      className="ar"
      style={{ width: w, height: h }}
    ></span>
  );
}

function sourceIcon(icon: "link" | "upload" | "music"): ReactNode {
  if (icon === "upload") return uploadIcon;
  if (icon === "link") return linkIcon;
  return noteIconSm;
}

/* ---- icons (stroke = currentColor so they follow the chip colour) ---- */
const svg = (d: ReactNode, size = 15) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    {d}
  </svg>
);
const noteIcon = svg(
  <>
    <path d="M9 18V6l12-2v12" />
    <circle cx="6" cy="18" r="3" />
    <circle cx="18" cy="16" r="3" />
  </>,
  18,
);
const noteIconSm = svg(
  <>
    <path d="M9 18V6l12-2v12" />
    <circle cx="6" cy="18" r="3" />
    <circle cx="18" cy="16" r="3" />
  </>,
);
const uploadIcon = svg(
  <>
    <path d="M12 16V4M6 10l6-6 6 6" />
    <path d="M4 20h16" />
  </>,
);
const linkIcon = svg(
  <>
    <path d="M10 13a5 5 0 0 0 7.5.5l3-3a5 5 0 0 0-7-7l-1.5 1.5" />
    <path d="M14 11a5 5 0 0 0-7.5-.5l-3 3a5 5 0 0 0 7 7l1.5-1.5" />
  </>,
);
const imagesIcon = svg(
  <>
    <rect x="3" y="5" width="18" height="14" rx="2" />
    <circle cx="8.5" cy="10" r="1.5" />
    <path d="M21 15l-5-5-8 8" />
  </>,
);
const chevron = (
  <svg
    className="chev"
    width="12"
    height="12"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M6 9l6 6 6-6" />
  </svg>
);
const globeIcon = svg(
  <>
    <circle cx="12" cy="12" r="9" />
    <path d="M3 12h18M12 3a14 14 0 0 1 0 18M12 3a14 14 0 0 0 0 18" />
  </>,
);
const lockIcon = svg(
  <>
    <rect x="5" y="11" width="14" height="10" rx="2" />
    <path d="M8 11V7a4 4 0 0 1 8 0v4" />
  </>,
  13,
);
const dotsIcon = svg(
  <>
    <circle cx="5" cy="12" r="1.4" fill="currentColor" />
    <circle cx="12" cy="12" r="1.4" fill="currentColor" />
    <circle cx="19" cy="12" r="1.4" fill="currentColor" />
  </>,
);
const captionsIcon = svg(
  <>
    <rect x="3" y="5" width="18" height="14" rx="2" />
    <path d="M7 15h5M14 15h3M7 11h3M12 11h5" />
  </>,
  14,
);
const xIcon = svg(<path d="M6 6l12 12M18 6L6 18" />, 12);
const arrowIcon = svg(<path d="M5 12h14M13 6l6 6-6 6" />, 14);
const playIcon = (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M7 4l14 8-14 8z" />
  </svg>
);
const pauseIcon = (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M6 4h4v16H6zM14 4h4v16h-4z" />
  </svg>
);
